import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { TextField } from 'office-ui-fabric-react';

import { KeyboardKeys } from '@app/AppConstants';
import { ContractType, PersonContractViewModel } from '@app/Models';
import { ValidationHelper } from '@app/Services/Validation';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { TextCell } from './TextCell';
import { ViewMode } from './ViewMode';

type InlinePercentageCellProps = {
    value: string;
    row: PersonContractViewModel;
    fieldKey: keyof PersonContractViewModel;
    onChange: Function;
    isReadOnly?: boolean;
};

@observer
export class InlinePercentageCell extends React.Component<InlinePercentageCellProps, {}> {
    private _store: InlinePercentageCellStore;

    constructor(props: InlinePercentageCellProps) {
        super(props);
        this._store = new InlinePercentageCellStore(props);
    }

    componentDidUpdate(prevProps: InlinePercentageCellProps) {
        if (this.props.row.id !== prevProps.row.id || this.props.value !== prevProps.value || this.props !== prevProps
            || this.props.isReadOnly !== prevProps.isReadOnly) {
            this._store.updateProps(this.props);
        }
    }

    render() {
        if (this._store.mode === ViewMode.view) {
            const value = this._store.value ? (this._store.value + '%') : '';
            return <TextCell value={value} onClick={this._store.toEditMode} />;
        } else {
            return (
                <TextField
                    onChange={this._store.onEdit}
                    maxLength={5}
                    onKeyDown={this._store.onKeyDown}
                    onBlur={this._store.onBlurHandler}
                    onKeyPress={ValidationHelper.validateFloat}
                    suffix="%"
                    autoFocus={true}
                    value={this._store.value}
                />
            );
        }
    }
}

class InlinePercentageCellStore {
    @observable value: string;
    @observable mode: string = ViewMode.view;
    private _initValue: string;
    private _currentProps: InlinePercentageCellProps;

    constructor(props: InlinePercentageCellProps) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.isReadOnly) return;
        this.mode = ViewMode.edit;
    };

    @action
    onEdit = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.value = newValue || '';
    };

    @action
    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === KeyboardKeys.ENTER) {
            this.onBlurHandler();
            event.preventDefault();
        }

        if (event.keyCode === KeyboardKeys.ESCAPE) {
            this.value = this._initValue || '';
            this.mode = ViewMode.view;
        }
    };

    @action
    onBlurHandler = () => {
        this.mode = ViewMode.view;
        if (!this.validateValue()) return;
               
        this._initValue = this.value;
        const row = this._currentProps.row as unknown as {[key: string]: string};
        row[this._currentProps.fieldKey] = this.value ? (Number(this.value) / 100).toFixed(4) : '';
        this._currentProps.onChange(this._currentProps.row, 'contract');
    };

    @action
    validateValue = () => {
        if (!this.value && this._currentProps.fieldKey === 'partTimePercentage' && this._currentProps.row.type === ContractType.Fix) {
            NotificationHandler.showWarning('Employment percentage can not be empty for Fix type ', 'Person Contract can not be updated');
            this.value = this._initValue;
            return false;
        }
        else if (this.value && parseInt(this.value) && this._currentProps.fieldKey === 'partTimePercentage' && this._currentProps.row.officePercentage && this._currentProps.row.officePercentage * 100 > parseInt(this.value)) {
            NotificationHandler.showWarning('Employment percentage can not be less than Office percentage ', 'Person Contract can not be updated');
            this.value = this._initValue;
            return false;
        }
        else if (this.value && parseInt(this.value) && this._currentProps.fieldKey === 'officePercentage' && this._currentProps.row.partTimePercentage && this._currentProps.row.partTimePercentage * 100 < parseInt(this.value)) {
            NotificationHandler.showWarning('Office percentage can not be greater than Employment percentage ', 'Person Contract can not be updated');
            this.value = this._initValue;
            return false;
        }
        else if (parseFloat(this.value) > 100) {
            this.value = '100';
        }

        return this._initValue !== this.value;
    };

    @action
    updateProps(props: InlinePercentageCellProps) {
        let value = props.value ? (Math.round(Number(props.value) * 100000) / 1000).toString() : '';
        this._initValue = value;
        this._currentProps = { ...props };
        this.value = value || ''; //`value` prop on `input` should not be null.
    }
}
